import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDisplayDateTime } from "utils/dateFormat";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { P } from "const/permissions";
import { hasPermissionKey } from "utils/permissionUtils";
import { formatMoney } from "utils/utils";
import { isRefundEntry } from "../Admissions/AddAdmissions/applicationFlow/termPaymentUtils";
import PaymentConflictAlert from "components/Payments/PaymentConflictAlert";
import { getPaymentConflict, isSettledConflict } from "utils/pendingPaymentChange";
import {
  rejectModulePayment,
  useAllPendingModulePaymentVerifications,
  verifyModulePayment,
} from "./verification/modulePaymentVerificationApi";

const paymentFromItem = (item) => item?.payment ?? item;

const ModulePaymentVerificationRequests = ({ onCountChange }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canVerify = hasPermissionKey(P.ACCOUNTS.PAYMENT_VERIFY);
  const [verifyDialog, setVerifyDialog] = useState({ open: false, item: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, item: null });
  const [rejectFeedback, setRejectFeedback] = useState("");
  // The server refused a decision because the payment changed while the dialog was open.
  const [conflict, setConflict] = useState(null);

  const {
    data: requests = [],
    isFetching,
    refetch,
  } = useAllPendingModulePaymentVerifications(true);

  useEffect(() => {
    onCountChange?.(requests.length);
  }, [onCountChange, requests.length]);

  const openClientAccount = useCallback(
    (item) => {
      const payment = paymentFromItem(item);
      if (!item?.accountMasterId && !item?.clientId) {
        toast.info("No linked account was found for this payment.");
        return;
      }
      navigate("/admin/account/add", {
        state: {
          item: {
            hasAccountDetails: true,
            clientAccountId: item.accountMasterId,
            clientData: item.clientId
              ? {
                  id: item.clientId,
                  name: item.clientName,
                }
              : null,
          },
          accountFocus: {
            openVerificationModal: true,
            moduleType: payment?.moduleType ?? null,
            moduleId: payment?.moduleId ?? null,
          },
        },
      });
    },
    [navigate]
  );

  const invalidateCaches = () => {
    queryClient.invalidateQueries({ queryKey: ["pending-module-payment-verifications"] });
    queryClient.invalidateQueries({ queryKey: ["pending-module-payment-verifications-all"] });
    queryClient.invalidateQueries({ queryKey: ["pending-payment-verifications"] });
    queryClient.invalidateQueries({ queryKey: ["cad-payment-transactions"] });
    queryClient.invalidateQueries({ queryKey: ["cad-payment-summary"] });
  };

  const closeVerify = () => {
    setVerifyDialog({ open: false, item: null });
    setConflict(null);
  };

  const closeReject = () => {
    setRejectDialog({ open: false, item: null });
    setRejectFeedback("");
    setConflict(null);
  };

  /**
   * An edit by the sender keeps the dialog open on the new figures so the verifier decides on what
   * they can see; an approval, rejection or withdrawal by someone else closes it.
   */
  const handleDecisionError = (error, fallback, dialogItem, setDialog, close) => {
    const found = getPaymentConflict(error);
    refetch();
    invalidateCaches();
    if (!found) {
      toast.error(
        error?.response?.data?.errorMessage || error?.response?.data?.message || fallback
      );
      return;
    }
    if (isSettledConflict(found)) {
      toast.info(found.message);
      close();
      return;
    }
    setConflict(found);
    if (found.payment) {
      setDialog({
        open: true,
        item: dialogItem?.payment ? { ...dialogItem, payment: found.payment } : found.payment,
      });
    }
  };

  const verifyMutation = useMutation({
    mutationFn: (payment) => verifyModulePayment(payment.id, payment.revision),
    onSuccess: () => {
      toast.success("Payment verified");
      closeVerify();
      refetch();
      invalidateCaches();
    },
    onError: (error) =>
      handleDecisionError(
        error,
        "Failed to verify payment",
        verifyDialog.item,
        setVerifyDialog,
        closeVerify
      ),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ payment, feedback }) =>
      rejectModulePayment(payment.id, feedback, payment.revision),
    onSuccess: () => {
      toast.success("Payment rejected");
      closeReject();
      refetch();
      invalidateCaches();
    },
    onError: (error) =>
      handleDecisionError(
        error,
        "Failed to reject payment",
        rejectDialog.item,
        setRejectDialog,
        closeReject
      ),
  });

  const conflictSummary = (item) => {
    const payment = paymentFromItem(item);
    if (!conflict || !payment) return null;
    return (
      <Typography variant="body2" sx={{ mt: 1.5 }}>
        Now: {isRefundEntry(payment.entryType) ? "refund" : "payment"} of{" "}
        <strong>{formatMoney(payment.amount)}</strong>
        {payment.date ? ` dated ${payment.date}` : ""}
        {payment.reference ? ` · Ref ${payment.reference}` : ""}
        {payment.notes ? ` · ${payment.notes}` : ""}
      </Typography>
    );
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Pending module payment verifications
        </Typography>
        <Button size="small" variant="outlined" onClick={() => refetch()} disabled={isFetching}>
          Refresh
        </Button>
      </Stack>

      <TableContainer className="border border-app-border rounded-lg">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Module</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {isFetching ? "Loading..." : "No pending module payment verifications."}
                </TableCell>
              </TableRow>
            ) : (
              requests.map((item) => {
                const payment = paymentFromItem(item);
                const refund = isRefundEntry(payment?.entryType);
                return (
                  <TableRow key={payment?.id ?? item.id} hover>
                    <TableCell>
                      <Link
                        component="button"
                        underline="hover"
                        onClick={() => openClientAccount(item)}
                        sx={{ textAlign: "left" }}
                      >
                        {item.clientName || `Client #${item.clientId}`}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <span>{item.moduleLabel || payment?.moduleType || "-"}</span>
                        {item.invoiceNumber && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Invoice ${item.invoiceNumber}`}
                            onClick={() => navigate(`/admin/invoice/view/${item.invoiceId}`)}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={refund ? "Refund" : "Payment"}
                        color={refund ? "warning" : "primary"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatMoney(payment?.amount)}</TableCell>
                    <TableCell>{formatDisplayDateTime(payment?.submittedAt)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => openClientAccount(item)}>
                          Open account
                        </Button>
                        {canVerify && (
                          <>
                            <Button
                              size="small"
                              color="success"
                              variant="contained"
                              onClick={() => setVerifyDialog({ open: true, item })}
                            >
                              Verify
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => setRejectDialog({ open: true, item })}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={verifyDialog.open} onClose={closeVerify}>
        <DialogTitle>Verify module payment?</DialogTitle>
        <DialogContent>
          <PaymentConflictAlert conflict={conflict} sx={{ mb: 1.5 }} />
          <Typography variant="body2" color="text.secondary">
            This will mark the{" "}
            {isRefundEntry(paymentFromItem(verifyDialog.item)?.entryType) ? "refund" : "payment"} as
            verified and update the account balances.
          </Typography>
          {conflictSummary(verifyDialog.item)}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVerify}>Cancel</Button>
          <Button
            color="success"
            variant="contained"
            disabled={verifyMutation.isPending}
            onClick={() => {
              const payment = paymentFromItem(verifyDialog.item);
              if (payment?.id) verifyMutation.mutate(payment);
            }}
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialog.open} onClose={closeReject}>
        <DialogTitle>Reject module payment</DialogTitle>
        <DialogContent>
          <PaymentConflictAlert conflict={conflict} sx={{ mb: 1.5 }} />
          {conflictSummary(rejectDialog.item)}
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Rejection feedback"
            value={rejectFeedback}
            onChange={(e) => setRejectFeedback(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReject}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectFeedback.trim() || rejectMutation.isPending}
            onClick={() => {
              const payment = paymentFromItem(rejectDialog.item);
              if (payment?.id) {
                rejectMutation.mutate({ payment, feedback: rejectFeedback.trim() });
              }
            }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModulePaymentVerificationRequests;
